import { useQuery } from "@apollo/client";
import { Maximize01, Minimize01 } from "@untitled-ui/icons-react";
import { useState } from "react";
import { CoachingDimensionNode } from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";

import { formatDate } from "./coaching-dashboard";
import { DimensionSection } from "./coaching-dimensions";
import type { ExpandOverride } from "./coaching-dimensions";
import getCoachingReportQuery from "./graphql/get-coaching-report-query";

const CoachingPage = ({
  profileId,
  managerId,
}: {
  profileId: number;
  managerId: number;
}) => {
  const { data, loading } = useQuery(getCoachingReportQuery, {
    variables: { subjectId: profileId, managerId },
  });

  const report = data?.coachingReport;

  const [expandOverride, setExpandOverride] = useState<ExpandOverride>(null);
  const [allExpanded, setAllExpanded] = useState(false);

  const handleToggleAll = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setExpandOverride((prev) => ({
      expanded: next,
      version: (prev?.version ?? 0) + 1,
    }));
  };

  return (
    <div className="w-full py-6 pb-12 px-4 sm:px-6 lg:px-8 bg-[#FCFCFD] min-h-full">
      {loading ? (
        <Loading>Loading coaching report...</Loading>
      ) : !report ? (
        <div className="py-12 text-center text-sm text-gray-500">
          Coaching report not yet available.
        </div>
      ) : (
        <div className="flex flex-col gap-12">
          {/* Page header */}
          <div className="flex items-start justify-between pl-4">
            <div className="flex flex-col gap-1">
              <h1 className="tf-header-2xl">Coaching Report</h1>
              {report.meta?.period?.start && report.meta?.period?.end && (
                <p className="text-sm text-gray-500">
                  {formatDate(report.meta.period.start)} –{" "}
                  {formatDate(report.meta.period.end)} ·{" "}
                  {(report.sources ?? []).length} sources
                </p>
              )}
            </div>
            <Button theme={buttonTheme.text} onClick={handleToggleAll}>
              {allExpanded ? (
                <>
                  <Minimize01 className="h-4 w-4" />
                  Collapse All
                </>
              ) : (
                <>
                  <Maximize01 className="h-4 w-4" />
                  Expand All
                </>
              )}
            </Button>
          </div>

          {/* Dimension deep dives */}
          {report.dimensions && report.dimensions.length > 0 && (
            <div className="flex flex-col gap-1">
              {(() => {
                const severityOrder: Record<string, number> = {
                  critical: 0,
                  alert: 1,
                  warn: 2,
                  good: 3,
                };
                const sorted = [...report.dimensions].sort(
                  (a: CoachingDimensionNode, b: CoachingDimensionNode) =>
                    (severityOrder[a.status ?? "good"] ?? 4) -
                    (severityOrder[b.status ?? "good"] ?? 4),
                );
                return sorted.map((dim: CoachingDimensionNode, i: number) => (
                  <DimensionSection
                    key={dim?.name ?? i}
                    dimension={dim}
                    dashboardCards={report.dashboard ?? []}
                    expandOverride={expandOverride}
                  />
                ));
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachingPage;
